import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountingPeriodDetailSchema,
  AccountingPeriodSchema,
  type AccountingPeriodDetailDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ApprovalPanel, workflowHistoryKey } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';

const PERIODS = '/api/v1/ledger/accounting-periods';
const PERIOD = 'ledger.accounting_period';

/**
 * Accounting periods (FRD-FIN-108 to FIN-110, FIN-115): every period with its dates and status.
 * An authorised user creates a period (it opens at once) and closes one — postings to a closed
 * period are refused. Reopening is asked for with a reason on the period's page and happens only
 * when the configured approval route approves it.
 */
export function AccountingPeriodsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${PERIOD}:create`);
  const [creating, setCreating] = useState(false);
  const periods = useQuery({
    queryKey: ['accounting-periods'],
    queryFn: () => getData(PERIODS, z.array(AccountingPeriodSchema)),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Accounting periods"
        description="Every posting falls in the period that covers its date; a closed period accepts none."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New period
            </Button>
          ) : undefined
        }
      />
      <Section>
        {periods.isPending && <LoadingState label="Loading accounting periods" />}
        {periods.isError && <ErrorState message={periods.error.message} />}
        {periods.data?.length === 0 && (
          <EmptyState
            title="No accounting periods"
            detail="Create the first period before anything can be posted."
          />
        )}
        {periods.data !== undefined && periods.data.length > 0 && (
          <Table size="small" aria-label="Accounting periods">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Fiscal year</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.data.map((period) => (
                <TableRow key={period.id} hover>
                  <TableCell>
                    <RecordLink to={`/periods/${period.id}`}>{period.code}</RecordLink>
                  </TableCell>
                  <TableCell>{period.fiscalYear}</TableCell>
                  <TableCell>{period.startDate}</TableCell>
                  <TableCell>{period.endDate}</TableCell>
                  <TableCell>{period.periodType}</TableCell>
                  <TableCell>
                    <StatusChip status={period.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {creating && (
        <CreatePeriodDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </Stack>
  );
}

function CreatePeriodDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [periodType, setPeriodType] = useState<'normal' | 'opening'>('normal');
  const create = useMutation({
    mutationFn: () =>
      postJson(
        PERIODS,
        {
          code: code.trim(),
          fiscalYear: Number.parseInt(fiscalYear, 10),
          startDate,
          endDate,
          periodType,
        },
        AccountingPeriodSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting-periods'] });
      onClose();
    },
  });
  const year = Number.parseInt(fiscalYear, 10);
  return (
    <FormDialog
      open
      title="New accounting period"
      submitLabel="Create period"
      canSubmit={
        code.trim() !== '' &&
        year >= 2000 &&
        year <= 2999 &&
        startDate !== '' &&
        endDate !== '' &&
        endDate >= startDate
      }
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Code"
        required
        value={code}
        helperText="As Finance names its periods, e.g. 2026-10"
        onChange={(e) => {
          setCode(e.target.value);
        }}
      />
      <TextField
        label="Fiscal year"
        required
        value={fiscalYear}
        onChange={(e) => {
          setFiscalYear(e.target.value);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          type="date"
          label="Starts"
          required
          fullWidth
          value={startDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
        />
        <TextField
          type="date"
          label="Ends"
          required
          fullWidth
          value={endDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
        />
      </Stack>
      <TextField
        select
        label="Type"
        value={periodType}
        helperText="Opening: the dated period opening balances post to (FRD-FIN-144)"
        onChange={(e) => {
          setPeriodType(e.target.value === 'opening' ? 'opening' : 'normal');
        }}
      >
        <MenuItem value="normal">Normal</MenuItem>
        <MenuItem value="opening">Opening</MenuItem>
      </TextField>
    </FormDialog>
  );
}

/**
 * One period (`/periods/:id`): its facts, Close for an open period, Request reopening for a
 * closed one (with a reason, to the approval route), the approval history and the audit trail of
 * every open, close and reopen event (FRD-FIN-115).
 */
export function PeriodDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, `${PERIOD}:change`);
  const [dialog, setDialog] = useState<'close' | 'reopen' | null>(null);
  const [reason, setReason] = useState('');
  const period = useQuery({
    queryKey: ['accounting-period', id],
    queryFn: () => getData(`${PERIODS}/${id}`, AccountingPeriodDetailSchema),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['accounting-period', id] });
    await queryClient.invalidateQueries({ queryKey: ['accounting-periods'] });
    await queryClient.invalidateQueries({ queryKey: workflowHistoryKey(PERIOD, id) });
    await queryClient.invalidateQueries({ queryKey: ['audit'] });
  };
  const act = useMutation({
    mutationFn: (input: { current: AccountingPeriodDetailDto; kind: 'close' | 'reopen' }) =>
      postJson(
        `${PERIODS}/${input.current.id}/${input.kind}`,
        input.kind === 'reopen' ? { reason: reason.trim() } : {},
        AccountingPeriodDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: input.current.version },
      ),
    onSuccess: async () => {
      setDialog(null);
      setReason('');
      await refresh();
    },
  });
  const back = (
    <Button component={RouterLink} to="/periods" startIcon={<ArrowBackIcon />}>
      All periods
    </Button>
  );
  if (period.isPending) return <LoadingState label="Loading the period" />;
  if (period.isError) {
    return (
      <RecordFailure title="Accounting period" noun="period" error={period.error} back={back} />
    );
  }
  const row = period.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Accounting period ${row.code}`}
        description={`${row.startDate} to ${row.endDate}`}
        actions={
          <>
            {back}
            {canChange && row.status === 'open' && (
              <Button
                variant="contained"
                color="warning"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('close');
                }}
              >
                Close period
              </Button>
            )}
            {/* One reopening at a time: the API refuses a second while one is in approval. */}
            {canChange && row.status === 'closed' && !row.reopeningPending && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('reopen');
                }}
              >
                Request reopening
              </Button>
            )}
          </>
        }
      />
      <Section title="Period">
        <Facts
          label="Period details"
          facts={[
            ['Code', row.code],
            ['Fiscal year', String(row.fiscalYear)],
            ['From', row.startDate],
            ['To', row.endDate],
            ['Type', row.periodType],
            ['Status', <StatusChip key="s" status={row.status} />],
            ['Closed at', row.closedAt ?? '—'],
            ...(row.reopeningPending
              ? ([['Reopening', 'Requested, awaiting approval']] as [string, string][])
              : []),
          ]}
        />
      </Section>
      <ApprovalPanel recordType={PERIOD} recordId={row.id} onDecided={() => void refresh()} />
      <RecordHistory recordType={PERIOD} recordId={row.id} />
      {dialog === 'close' && (
        <ConfirmDialog
          open
          title={`Close ${row.code}?`}
          confirmLabel="Close period"
          busy={act.isPending}
          error={act.isError ? act.error.message : null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            act.mutate({ current: row, kind: 'close' });
          }}
          message={'Postings dated in this period will be refused. Reopening needs approval.'}
        />
      )}
      {dialog === 'reopen' && (
        <FormDialog
          open
          title={`Request reopening of ${row.code}`}
          submitLabel="Submit for approval"
          canSubmit={reason.trim() !== ''}
          submitting={act.isPending}
          error={act.isError ? act.error.message : null}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={() => {
            act.mutate({ current: row, kind: 'reopen' });
          }}
        >
          <Typography variant="body2">
            The period stays closed until the approval route approves the reopening.
          </Typography>
          <TextField
            label="Reason"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </FormDialog>
      )}
    </Stack>
  );
}
